import React, { useEffect, useMemo, useState } from "react";
import { savedWisdom } from "../lib/api";
import useSubscriptionStatus from "../lib/useSubscriptionStatus";
import { toast } from "sonner";
import PaywallModal from "../components/PaywallModal";
import { Bookmark, BookOpenText, Sparkles, MessageSquareText, Sprout, Trash2, Lock } from "lucide-react";

const KIND_META = {
  devotional: { label: "Devotional", icon: Sparkles, tone: "bg-[#F5EBD9] text-[#8C7A4F] border-[#e8dcc4]" },
  scripture: { label: "Scripture", icon: BookOpenText, tone: "bg-[#EAF1F2] text-[#4F7078] border-[#d6e2e4]" },
  wisdom_path: { label: "Wisdom Path", icon: Sprout, tone: "bg-[#E1ECDD] text-[#4F6F52] border-[#d3e0d0]" },
  guidance: { label: "Guidance", icon: MessageSquareText, tone: "bg-[#EEF3EC] text-[#4F6F52] border-[#dce5db]" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "devotional", label: "Devotionals" },
  { key: "scripture", label: "Scripture" },
  { key: "wisdom_path", label: "Wisdom Path" },
  { key: "guidance", label: "Guidance" },
];

function useHasWriteAccess() {
  return useSubscriptionStatus().isPaid;
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function KindBadge({ kind }) {
  const m = KIND_META[kind] || KIND_META.devotional;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${m.tone}`}
    >
      <Icon size={11} strokeWidth={2.2} /> {m.label}
    </span>
  );
}

function WisdomCard({ item, canWrite, onDelete, onLockedWrite }) {
  return (
    <article
      data-testid={`wisdom-item-${item.id}`}
      className="rounded-3xl border border-[#DCE5DB] bg-white p-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <KindBadge kind={item.kind} />
            <span className="text-[11px] text-[#6B6B6B]">{formatWhen(item.created_at)}</span>
          </div>
          <h3 className="font-display text-lg text-[#2E2E2E] leading-snug">{item.title}</h3>
        </div>
        <button
          data-testid={`wisdom-delete-${item.id}`}
          onClick={() => (canWrite ? onDelete(item) : onLockedWrite())}
          className="p-2 rounded-full text-[#6B6B6B] hover:text-[#A86B4A] hover:bg-[#F5E6D3]/60 transition shrink-0"
          aria-label="Remove from Saved Wisdom"
          title={canWrite ? "Remove" : "Upgrade to remove"}
        >
          <Trash2 size={15} />
        </button>
      </div>
      <p className="text-[15px] leading-[1.75] text-[#2E2E2E] whitespace-pre-wrap">{item.body}</p>
      {item.meta?.scripture_reference && (
        <p className="mt-3 text-sm font-medium text-[#4F6F52]">— {item.meta.scripture_reference}</p>
      )}
    </article>
  );
}

export default function SavedWisdomPage() {
  const canWrite = useHasWriteAccess();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  const load = async () => {
    try {
      const rows = await savedWisdom.list();
      setItems(rows);
    } catch {
      setItems([]);
      toast.error("Couldn't load your Saved Wisdom");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    if (filter === "all") return items;
    return items.filter((x) => x.kind === filter);
  }, [items, filter]);

  const openPaywall = () => setPaywall(true);

  const handleDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await savedWisdom.remove(confirmDel.id);
      setItems((rows) => rows.filter((r) => r.id !== confirmDel.id));
      setConfirmDel(null);
      toast.success("Removed");
    } catch (err) {
      if (err?.response?.status === 402) {
        setConfirmDel(null);
        openPaywall();
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't remove item");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="fade-in-up">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
            <Bookmark size={17} strokeWidth={1.8} />
          </span>
          <h1 className="font-display text-3xl text-[#2E2E2E]">Saved Wisdom</h1>
        </div>
        <p className="text-sm text-[#6B6B6B] leading-relaxed mt-2">
          Devotionals, scripture, and guidance you wanted to keep close.
        </p>
      </header>

      {!canWrite && (
        <div
          data-testid="wisdom-readonly-banner"
          className="rounded-3xl border border-[#D6C7A1]/60 bg-[#F5EBD9]/50 p-5 flex items-start gap-3 fade-in-up"
        >
          <span className="w-9 h-9 shrink-0 rounded-2xl bg-white/70 text-[#8C7A4F] flex items-center justify-center">
            <Lock size={15} />
          </span>
          <div className="flex-1">
            <p className="font-medium text-[#2E2E2E] text-[15px]">
              Your Wisdom library is saved and read-only
            </p>
            <p className="text-sm text-[#6B6B6B] leading-relaxed mt-1">
              Upgrade to LifeGuide Pro to keep saving new devotionals, scripture, and guidance moments.
            </p>
            <button
              data-testid="wisdom-upgrade-btn"
              onClick={openPaywall}
              className="mt-3 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              Continue with LifeGuide Pro
            </button>
          </div>
        </div>
      )}

      <div
        data-testid="wisdom-filters"
        className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 fade-in-up"
      >
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              data-testid={`wisdom-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                active
                  ? "bg-[#4F6F52] text-white border-[#4F6F52]"
                  : "bg-white text-[#4E5A4C] border-[#DCE5DB] hover:border-[#4F6F52]/40"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {filtered === null ? (
        <div className="space-y-3">
          <div className="h-28 rounded-3xl shimmer" />
          <div className="h-28 rounded-3xl shimmer" />
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="wisdom-empty-state"
          className="rounded-3xl border border-dashed border-[#DCE5DB] bg-white p-8 text-center fade-in-up"
        >
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[#EEF3EC] text-[#4F6F52] flex items-center justify-center mb-3">
            <Bookmark size={20} strokeWidth={1.8} />
          </div>
          <h3 className="font-display text-xl text-[#2E2E2E] mb-1.5">
            {filter === "all" ? "Nothing saved yet" : "Nothing here yet"}
          </h3>
          <p className="text-sm text-[#6B6B6B] max-w-xs mx-auto leading-relaxed">
            Tap the bookmark on any devotional, scripture, wisdom path, or guidance moment to save it here.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="wisdom-list">
          {filtered.map((item) => (
            <WisdomCard
              key={item.id}
              item={item}
              canWrite={canWrite}
              onDelete={(i) => setConfirmDel(i)}
              onLockedWrite={openPaywall}
            />
          ))}
        </div>
      )}

      {confirmDel && (
        <div
          data-testid="wisdom-confirm-delete"
          className="fixed inset-0 z-[100] bg-[#2E2E2E]/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDel(null)}
        >
          <div
            className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-[0_-8px_40px_rgba(0,0,0,0.12)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl text-[#2E2E2E] mb-2">Remove from Saved Wisdom?</h3>
            <p className="text-sm text-[#6B6B6B] leading-relaxed mb-5">
              You can save it again anytime from where you found it.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="wisdom-delete-cancel"
                onClick={() => setConfirmDel(null)}
                className="text-sm text-[#6B6B6B] hover:text-[#2E2E2E] rounded-full px-4 py-2 hover:bg-[#EEF3EC] transition"
              >
                Keep it
              </button>
              <button
                data-testid="wisdom-delete-confirm"
                onClick={handleDelete}
                disabled={busy}
                className="bg-[#A86B4A] hover:bg-[#8f5a3d] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium transition inline-flex items-center gap-1.5"
              >
                <Trash2 size={13} /> {busy ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      <PaywallModal
        open={paywall}
        reason="Your LifeGuide journey is saved. Upgrade to LifeGuide Pro to keep saving wisdom you want to remember."
        onClose={() => setPaywall(false)}
      />
    </div>
  );
}
